import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useOrpc } from './useClient'

/** Execution is asynchronous (the claim-submit task) — refetch after a short grace period. */
const REFETCH_DELAY_MS = 5000

/**
 * The Routing column's "Submit claim"/"Retry" action: enqueues one claim-submit task run
 * via billingV2SubmitClaim. `enqueued: true` only means the run was queued — the outcome
 * (billed + claimSubmittedAt, or failed + submitLastError) lands on the case row later,
 * so the list/detail queries are invalidated on a delay rather than immediately.
 * `enqueued: false` is the handler's pre-check refusing a stale row (the button only
 * shows on submittable cases) — the reason code is shown as-is, and a content refusal
 * (the Claim's own problems, as sentences) is shown as the message.
 *
 * `providerLabel` is the route's display label (Stedi/Bridge) for the notification.
 */
export function useSubmitClaim(providerLabel: string) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.billingV2SubmitClaim.mutationOptions({
      onSuccess: (result) => {
        if (!result.enqueued) {
          const reason = result.reason ?? 'unknown'
          // A content refusal (PD-2013) is already a sentence telling the biller what to fix.
          showNotification({
            color: 'red',
            ...(/\s/.test(reason)
              ? { title: 'Claim was not submitted', message: reason }
              : { message: `Claim was not submitted (${reason}).` }),
          })
          return
        }

        showNotification({
          color: 'green',
          message: `Submission started — the claim was queued for ${providerLabel}; the status will refresh shortly.`,
        })

        // The page-level queryClient outlives the row, so firing after unmount is safe.
        setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: orpc.billingV2ListCases.key() })
          void queryClient.invalidateQueries({ queryKey: orpc.billingV2CaseDetail.key() })
        }, REFETCH_DELAY_MS)
      },
      onError: (error) => {
        showNotification({
          color: 'red',
          message: error instanceof Error ? error.message : 'Failed to submit the claim',
        })
      },
    }),
  )
}
